import Icon from "@ant-design/icons";
import { Avatar, Button, Divider, notification, Popconfirm, Tag } from "antd";
import FileIcon from "icons/FileIcon";
import LocationIcon from "icons/LocationIcon";
import LocationPlusIcon from "icons/LocationPlusIcon";
import PenIcon from "icons/PenIcon";
import UploadFileIcon from "icons/UploadFileIcon";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetUserAddress, useUpdateShippingAddress } from "services/address";
import { useGetUserMe, useUpdateUser } from "services/user";
import { IShippingAddress } from "types/address";
import { getFullAddress } from "utils/address";
import AddressModal from "./AddressModal";
import UserDetailModal from "./UserDetailModal";
import { isImageFile } from "utils/image";
import { E_USER_TIER } from "constants/common";
import { useUploadImage } from "services/image";
import { VITE_API_ENDPOINT } from "src/environments";
import ImageAuthen from "./ImageAuthen";
// import { useMediaQuery } from "react-responsive";
import StarIcon from "icons/StarIcon";

const UserProfileForm = () => {
  const licenseInputRef = useRef<HTMLInputElement | null>(null);
  const certificateInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const { updateUserAsync } = useUpdateUser();
  const { userInfo } = useGetUserMe();

  const isGeneral = userInfo?.userTierId === E_USER_TIER.GENERAL;
  const { userAddress } = useGetUserAddress();
  const [isShowAddressModal, setIsShowAddressModal] = useState<boolean>(false);
  const [isShowUserDetailModal, setIsShowUserDetailModal] =
    useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<IShippingAddress>();
  const { uploadImageAsync, isPending } = useUploadImage();

  // Check if the device is mobile
  // const isMobile = useMediaQuery({ maxWidth: 767 });

  const { updateShippingAddressAsync } = useUpdateShippingAddress();

  const handleAddNewAddress = () => {
    setSelectedAddress(undefined);
    setIsShowAddressModal(true);
  };

  const handleCloseAddressModal = () => {
    setSelectedAddress(undefined);
    setIsShowAddressModal(false);
  };

  const handleUpdateDefaultShippingAddress = async (id: number) => {
    await updateShippingAddressAsync({ id: id, isDefault: true });
    notification.success({
      message: "แก้ไขที่อยู่หลักสำเร็จ",
      placement: "topRight",
    });
  };

  const handleButtonLicenseClick = () => {
    licenseInputRef.current?.click(); // Trigger the hidden file input click
  };

  const handleButtonCertificateClick = () => {
    certificateInputRef.current?.click(); // Trigger the hidden file input click
  };

  const handleFileChange = (
    key: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const fileList = event.target.files;
    const file = fileList?.[0];
    if (file) {
      uploadImageAsync(file, {
        onSuccess: async (data) => {
          updateUserAsync({
            [key]: `${VITE_API_ENDPOINT}/image-upload/${encodeURIComponent(
              data.data.imageUrl
            )}`,
          });
        },
        onError: () => {},
      });
    }
  };

  if (!userInfo) {
    return <div></div>;
  }

  return (
    <div className="user-profile-form min-h-screen">
      <div className="flex gap-4 flex-wrap">
        <div className="relative">
          <div className="absolute flex items-center justify-center top-16 left-16">
            <Avatar size={64} className="absolute">
              {userInfo.fullName[0]}
            </Avatar>
          </div>
          <svg className="w-32 h-32">
            <circle
              cx="50%"
              cy="50%"
              r="48"
              fill="none"
              stroke="#9448FF"
              strokeWidth="1"
              strokeDasharray="10 5"
            />
          </svg>
        </div>
        <div className="flex flex-col justify-center">
          <div className="font-semibold">{userInfo.fullName}</div>
          {!isGeneral && (
            <div>
              <p>ชื่อสถานประกอบการ</p>
              <p className="font-semibold">{userInfo.storeName}</p>
            </div>
          )}

          {/* Capsule Points Display */}
          <div className="mt-2">
            <Tag
              color="#9448FF"
              className="px-3 py-1 rounded-full flex items-center w-[80px] justify-center cursor-pointer"
              onClick={() => navigate("/points-history")}
            >
              <StarIcon />
              <span>{userInfo.capsulePoints || 0}</span>
            </Tag>
          </div>
        </div>
      </div>
      <div className="flex gap-4">
        <Divider
          variant="dashed"
          orientation="left"
          style={{ borderColor: "#9448FF" }}
          orientationMargin="0"
          className="form-header"
        >
          ข้อมูลที่อยู่จัดส่ง
        </Divider>
      </div>

      {userAddress.map((address) => {
        return (
          <div className="relative" key={address.id}>
            {address.isDefault && (
              <Icon
                component={LocationIcon}
                className="absolute text-primary"
              />
            )}
            <div className="grid grid-cols-2 md:grid-cols-3">
              <div className="col-span-2 pl-8 px-4 md:px-12">
                <div>
                  <span>ชื่อผู้รับสินค้า : </span>
                  <span>{address.fullName} </span>
                </div>
                <div>
                  <span>ที่อยู่รับพัสดุ : </span>
                  <span>
                    {getFullAddress({
                      building: address.building,
                      moo: address.moo,
                      road: address.road,
                      address: address.address,
                      district: address.district,
                      postCode: address.postCode,
                      province: address.province,
                      subdistrict: address.subdistrict,
                    })}
                  </span>
                </div>
                <div>
                  <span>เบอร์โทรติดต่อ : </span>
                  <span>{address.phoneNumber}</span>
                </div>
              </div>
              <div className="col-span-2 mt-2 md:col-span-1 text-end">
                {!address.isDefault && (
                  <Popconfirm
                    title="ต้องการเปลี่ยนที่อยู่หลักใช่หรือไม่"
                    onConfirm={() =>
                      handleUpdateDefaultShippingAddress(address.id)
                    }
                    okText="ใช่"
                    cancelText="ยกเลิก"
                    okButtonProps={{
                      className: "text-sm py-4 px-5",
                    }}
                    cancelButtonProps={{
                      className: "text-sm py-4 px-5",
                    }}
                  >
                    <Button size="large" className="px-5 mb-4">
                      <LocationIcon />
                      <span className="text-sm text-black">
                        ตั้งค่าเป็นที่อยู่หลัก
                      </span>
                    </Button>
                  </Popconfirm>
                )}
                <Button
                  size="large"
                  className="px-5 ml-4"
                  onClick={() => {
                    setSelectedAddress(address);
                    setIsShowAddressModal(true);
                  }}
                >
                  <PenIcon />
                  <span className="text-sm text-black">แก้ไขที่อยู่</span>
                </Button>
              </div>
            </div>
            {/* <div className="flex justify-between items-center">
              <div className="px-12">
                <div>
                  <span>ชื่อผู้รับสินค้า : </span>
                  <span>{address.fullName} </span>
                </div>
                <div>
                  <span>ที่อยู่รับพัสดุ : </span>
                  <span>
                    {getFullAddress({
                      building: address.building,
                      moo: address.moo,
                      road: address.road,
                      address: address.address,
                      district: address.district,
                      postCode: address.postCode,
                      province: address.province,
                      subdistrict: address.subdistrict,
                    })}
                  </span>
                </div>
                <div>
                  <span>เบอร์โทรติดต่อ : </span>
                  <span>{address.phoneNumber}</span>
                </div>
              </div>
              <div className="flex flex-col">
                {!address.isDefault && (
                  <Popconfirm
                    title="ต้องการเปลี่ยนที่อยู่หลักใช่หรือไม่"
                    onConfirm={() =>
                      handleUpdateDefaultShippingAddress(address.id)
                    }
                    okText="ใช่"
                    cancelText="ยกเลิก"
                    okButtonProps={{
                      className: "text-sm py-4 px-5",
                    }}
                    cancelButtonProps={{
                      className: "text-sm py-4 px-5",
                    }}
                  >
                    <Button
                      size="large"
                      className="px-5 mt-4 flex justify-start"
                    >
                      <LocationIcon />
                      <span className="text-sm text-black">
                        ตั้งค่าเป็นที่อยู่หลัก
                      </span>
                    </Button>
                  </Popconfirm>
                )}
                <Button
                  size="large"
                  className="px-5 mt-4 flex justify-start"
                  onClick={() => {
                    setSelectedAddress(address);
                    setIsShowAddressModal(true);
                  }}
                >
                  <PenIcon />
                  <span className="text-sm text-black">แก้ไขที่อยู่</span>
                </Button>
              </div>
            </div> */}
            <Divider variant="dashed" style={{ borderColor: "##9448FF33" }} />
          </div>
        );
      })}
      <Button size="large" className="px-5 mt-4" onClick={handleAddNewAddress}>
        <LocationPlusIcon />
        <span className="text-sm text-black">เพิ่มที่อยู่จัดส่ง</span>
      </Button>
      <div className="flex gap-4">
        <Divider
          variant="dashed"
          orientation="left"
          style={{ borderColor: "#9448FF" }}
          orientationMargin="0"
          className="form-header"
        >
          ข้อมูลติดต่อ
        </Divider>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3">
        <div className="col-span-2 px-4 md:px-12">
          <div>
            <span>ที่อยู่ : </span>
            <span>
              {userInfo
                ? getFullAddress({
                    building: userInfo.building,
                    moo: userInfo.moo,
                    road: userInfo.road,
                    address: userInfo.address,
                    district: userInfo.district,
                    postCode: userInfo.postCode,
                    province: userInfo.province,
                    subdistrict: userInfo.subdistrict,
                  })
                : "-"}
            </span>
          </div>
          <div>
            <span>เบอร์โทร : </span>
            <span>{userInfo.phoneNumber}</span>
          </div>
          <div>
            <span>อีเมล : </span>
            <span>{userInfo.email}</span>
          </div>
          <div>
            <span>LINE ID : </span>
            <span>{userInfo.lineId}</span>
          </div>
        </div>
        <div className="col-span-2 mt-2 md:col-span-1 text-end">
          <Button
            size="large"
            className="px-5 mt-4"
            onClick={() => setIsShowUserDetailModal(true)}
          >
            <PenIcon />
            <span className="text-sm text-black">แก้ไขข้อมูลติดต่อ</span>
          </Button>
        </div>
      </div>
      {!isGeneral && (
        <div>
          <div className="flex gap-4">
            <Divider
              variant="dashed"
              orientation="left"
              style={{ borderColor: "#9448FF" }}
              orientationMargin="0"
              className="form-header"
            >
              ใบรับรองประกอบวิชาชีพ
            </Divider>
          </div>
          <div className="flex justify-between px-4 md:px-12">
            <div className="flex items-center gap-4">
              {userInfo.licenseImageKey &&
              isImageFile(userInfo.licenseImageKey) ? (
                <ImageAuthen url={userInfo.licenseImageKey} />
              ) : (
                <div className="p-4 bg-[#F8F8F8] w-20 h-20 flex justify-center border-dashed border-[#929191] rounded-lg">
                  <Icon component={FileIcon} className="text-[18px]" />
                </div>
              )}
              <span>ใบประกอบวิชาชีพ</span>
            </div>
            <Button
              size="large"
              className="px-5 mt-4"
              onClick={handleButtonLicenseClick}
              loading={isPending}
            >
              <UploadFileIcon />
              <span className="text-sm text-black">อัพโหลดไฟล์</span>
            </Button>
            <input
              type="file"
              ref={licenseInputRef}
              style={{ display: "none" }} // Hide the file input
              onChange={(event) => handleFileChange("licenseImageKey", event)}
            />
          </div>
          <div className="flex gap-4">
            <Divider
              variant="dashed"
              orientation="left"
              style={{ borderColor: "#9448FF" }}
              orientationMargin="0"
              className="form-header"
            >
              ใบอนุญาต
            </Divider>
          </div>
          <div className="flex justify-between px-4 md:px-12">
            <div className="flex items-center gap-4">
              {userInfo.certificateImageKey &&
              isImageFile(userInfo.certificateImageKey) ? (
                <ImageAuthen url={userInfo.certificateImageKey} />
              ) : (
                <div className="p-4 bg-[#F8F8F8] w-20 h-20 flex justify-center border-dashed border-[#929191] rounded-lg">
                  <Icon component={FileIcon} className="text-[18px]" />
                </div>
              )}

              <span>ใบอนุญาต</span>
            </div>
            <Button
              size="large"
              className="px-5 mt-4"
              onClick={handleButtonCertificateClick}
              loading={isPending}
            >
              <UploadFileIcon />
              <span className="text-sm text-black">อัพโหลดไฟล์</span>
            </Button>
            <input
              type="file"
              ref={certificateInputRef}
              style={{ display: "none" }} // Hide the file input
              onChange={(event) =>
                handleFileChange("certificateImageKey", event)
              }
            />
          </div>
        </div>
      )}
      <AddressModal
        isOpen={isShowAddressModal}
        onClose={handleCloseAddressModal}
        data={selectedAddress}
      />

      <UserDetailModal
        isOpen={isShowUserDetailModal}
        onClose={() => setIsShowUserDetailModal(false)}
        data={{
          id: userInfo.id,
          address: userInfo.address,
          phoneNumber: userInfo.phoneNumber,
          province: userInfo.province,
          district: userInfo.district,
          subdistrict: userInfo.subdistrict,
          postCode: userInfo.postCode,
          lineId: userInfo.lineId,
        }}
      />
    </div>
  );
};
export default UserProfileForm;
